import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface UserTag {
  id: string;
  name: string;
  color: string;
  description: string | null;
  is_system: boolean;
  tag_type: 'dynamic' | 'manual';
  created_at: string;
}

export interface UserTagAssignment {
  id: string;
  user_id: string;
  tag_id: string;
  assigned_at: string;
  assigned_by: string | null;
  source: string;
  tag: UserTag;
}

const USER_TAGS_QUERY_KEY = "user-tags";
const USER_TAG_ASSIGNMENTS_QUERY_KEY = "user-tag-assignments";

export const useUserTags = () => {
  const queryClient = useQueryClient();

  // Fetch all available tags
  const { data: allTags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: [USER_TAGS_QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_tags")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data as UserTag[];
    },
  });

  // Fetch all tag assignments (for admin view)
  const { data: allAssignments = [], isLoading: isLoadingAssignments } = useQuery({
    queryKey: [USER_TAG_ASSIGNMENTS_QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_tag_assignments")
        .select(`
          id,
          user_id,
          tag_id,
          assigned_at,
          assigned_by,
          source,
          tag:user_tags (*)
        `);
      
      if (error) throw error;
      return data as unknown as UserTagAssignment[];
    },
  });

  // Group assignments by user
  const assignmentsByUser = allAssignments.reduce((acc, assignment) => {
    if (!acc[assignment.user_id]) {
      acc[assignment.user_id] = [];
    }
    acc[assignment.user_id].push(assignment);
    return acc;
  }, {} as Record<string, UserTagAssignment[]>);

  // Manage user tags mutation (single user)
  const manageUserTags = useMutation({
    mutationFn: async ({
      userId,
      tagsToAdd,
      tagsToRemove,
    }: {
      userId: string;
      tagsToAdd: string[];
      tagsToRemove: string[];
    }) => {
      const { data, error } = await supabase.functions.invoke("admin-user-actions", {
        body: {
          action: "manage_tags",
          userId,
          data: {
            tags_to_add: tagsToAdd,
            tags_to_remove: tagsToRemove,
          },
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_TAG_ASSIGNMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-logs"] });
      toast.success("Tags updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update tags: ${error.message}`);
    },
  });

  // Bulk manage tags for multiple users
  const bulkManageUserTags = useMutation({
    mutationFn: async ({
      userIds,
      tagsToAdd,
      tagsToRemove,
    }: {
      userIds: string[];
      tagsToAdd: string[];
      tagsToRemove: string[];
    }) => {
      const { data, error } = await supabase.functions.invoke("admin-user-actions", {
        body: {
          action: "bulk_manage_tags",
          data: {
            user_ids: userIds,
            tags_to_add: tagsToAdd,
            tags_to_remove: tagsToRemove,
          },
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [USER_TAG_ASSIGNMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-logs"] });
      toast.success(`Tags updated for ${data.user_count} users`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to bulk update tags: ${error.message}`);
    },
  });

  return {
    allTags,
    isLoadingTags,
    allAssignments,
    isLoadingAssignments,
    assignmentsByUser,
    manageUserTags: manageUserTags.mutateAsync,
    isManagingTags: manageUserTags.isPending,
    bulkManageUserTags: bulkManageUserTags.mutateAsync,
    isBulkManaging: bulkManageUserTags.isPending,
  };
};

// Helper function to get tag color class
export const getTagColorClass = (color: string): string => {
  const colorMap: Record<string, string> = {
    purple: "bg-purple-500/20 text-purple-700 border-purple-500/30",
    pink: "bg-pink-500/20 text-pink-700 border-pink-500/30",
    green: "bg-green-500/20 text-green-700 border-green-500/30",
    emerald: "bg-emerald-500/20 text-emerald-700 border-emerald-500/30",
    blue: "bg-blue-500/20 text-blue-700 border-blue-500/30",
    amber: "bg-amber-500/20 text-amber-700 border-amber-500/30",
    teal: "bg-teal-500/20 text-teal-700 border-teal-500/30",
    indigo: "bg-indigo-500/20 text-indigo-700 border-indigo-500/30",
    violet: "bg-violet-500/20 text-violet-700 border-violet-500/30",
    gray: "bg-gray-500/20 text-gray-700 border-gray-500/30",
    red: "bg-red-500/20 text-red-700 border-red-500/30",
    orange: "bg-orange-500/20 text-orange-700 border-orange-500/30",
    cyan: "bg-cyan-500/20 text-cyan-700 border-cyan-500/30",
    rose: "bg-rose-500/20 text-rose-700 border-rose-500/30",
  };
  return colorMap[color] || colorMap.gray;
};
